/**
 * CRISPR PAM 走査と簡易規則（§5.9 I94–I102）。論文完全再現はしない。
 */

import { CODE_TO_CHAR, countGc } from '../sequence/nucleotideCodes';
import { encodeIupacPattern, reverseComplementIupac } from './iupac';
import { searchCodes } from './patternSearch';
import type { Topology } from '../sequence/interval';
import type { Interval } from '../sequence/interval';

export interface CrisprNuclease {
  id: string;
  name: string;
  pam: string;
  spacer: number;
  pamSide: "5'" | "3'";
}

export const CRISPR_NUCLEASES: CrisprNuclease[] = [
  { id: 'spcas9', name: 'SpCas9', pam: 'NGG', spacer: 20, pamSide: "3'" },
  { id: 'vrqr', name: 'VRQR', pam: 'NGA', spacer: 20, pamSide: "3'" },
  { id: 'qqr1', name: 'QQR1', pam: 'NAAN', spacer: 20, pamSide: "3'" },
  { id: 'sacas9', name: 'SaCas9', pam: 'NNGRRT', spacer: 21, pamSide: "3'" },
  { id: 'nmecas9', name: 'NmeCas9', pam: 'NNNNGATT', spacer: 24, pamSide: "3'" },
  { id: 'stcas9', name: 'StCas9', pam: 'NNAGAAW', spacer: 20, pamSide: "3'" },
  { id: 'cas12a', name: 'Cas12a', pam: 'TTTV', spacer: 20, pamSide: "5'" },
  { id: 'cas12b', name: 'Cas12b', pam: 'TTN', spacer: 20, pamSide: "5'" },
];

export interface CrisprGuide {
  start: number;
  end: number;
  strand: 1 | -1;
  spacer: string;
  pam: string;
  gc: number;
  score: number;
  reasons: string[];
}

export function findCrisprGuides(
  codes: Uint8Array,
  options: {
    genomeLength: number;
    topology: Topology;
    interval: Interval;
    nuclease?: CrisprNuclease;
    limit?: number;
  }
): CrisprGuide[] {
  const nuclease = options.nuclease ?? CRISPR_NUCLEASES[0];
  const pamBits = encodeIupacPattern(nuclease.pam);
  if (!pamBits) return [];
  const limit = options.limit ?? 80;
  const hits = searchCodes(codes, {
    genomeLength: options.genomeLength,
    topology: options.topology,
    interval: options.interval,
    pattern: nuclease.pam,
    bothStrands: true,
    limit: 400,
  });

  const guides: CrisprGuide[] = [];
  for (const hit of hits) {
    const guide = extractGuide(codes, hit.start, hit.strand, nuclease, options.genomeLength);
    if (guide) guides.push(guide);
    if (guides.length >= limit) break;
  }
  return guides.sort((a, b) => b.score - a.score);
}

function extractGuide(
  codes: Uint8Array,
  pamStart: number,
  strand: 1 | -1,
  nuclease: CrisprNuclease,
  genomeLength: number
): CrisprGuide | null {
  const pamLen = nuclease.pam.length;
  let spacerStart: number;
  let spacerEnd: number;
  if (nuclease.pamSide === "3'") {
    if (strand === 1) {
      spacerEnd = pamStart;
      spacerStart = spacerEnd - nuclease.spacer;
    } else {
      spacerStart = pamStart + pamLen;
      spacerEnd = spacerStart + nuclease.spacer;
    }
  } else if (strand === 1) {
    spacerStart = pamStart + pamLen;
    spacerEnd = spacerStart + nuclease.spacer;
  } else {
    spacerEnd = pamStart;
    spacerStart = spacerEnd - nuclease.spacer;
  }
  if (spacerStart < 0 || spacerEnd > genomeLength) return null;

  const spacerCodes = codes.subarray(spacerStart, spacerEnd);
  if (spacerCodes.length !== nuclease.spacer) return null;
  const spacer = Array.from(spacerCodes, (code) => CODE_TO_CHAR[code]).join('');
  const pam =
    strand === 1
      ? Array.from(codes.subarray(pamStart, pamStart + pamLen), (code) => CODE_TO_CHAR[code]).join('')
      : reverseComplementIupac(
          Array.from(codes.subarray(pamStart, pamStart + pamLen), (code) => CODE_TO_CHAR[code]).join('')
        );
  const gc = spacerCodes.length === 0 ? 0 : (countGc(spacerCodes) / spacerCodes.length) * 100;
  const reasons: string[] = [];
  let score = 70;
  if (gc < 30 || gc > 80) {
    reasons.push('GC 30–80% の外');
    score -= 25;
  }
  if (/(.)\1{3,}/.test(spacer)) {
    reasons.push('ホモポリマー');
    score -= 15;
  }
  if (spacer[0] === 'G') {
    reasons.push('5′G');
    score += 5;
  }
  return {
    start: Math.min(spacerStart, pamStart),
    end: Math.max(spacerEnd, pamStart + pamLen),
    strand,
    spacer,
    pam,
    gc: Math.round(gc * 10) / 10,
    score: Math.max(0, Math.min(100, score)),
    reasons,
  };
}
